/** @format */
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useContext, useState } from 'react';

import { formatAsCurrency } from '@toucan/helpers/currency';
import { isServer } from '@toucan/helpers/environment';
import { getNextQuoteItemId, isInQuote } from '@toucan/helpers/quote';

import { QuoteItemRepeaterValue } from '@toucan/components/molecules/quote-item-repeater';

import { SellFormContext } from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';
import { ProductSummaryV2 } from '@toucan/client/global/product';

type UseTradeForThisHandlerProps = {
    productData: ProductSummaryV2;
    refreshProductData?: (productId: number) => Promise<ProductSummaryV2>;
};

export const useTradeForThisHandler = ({
    productData,
    refreshProductData,
}: UseTradeForThisHandlerProps) => {
    const router = useRouter();

    const { t } = useTranslation();

    const { activeMarket } = useContext(MarketContext);

    const {
        sellForm: { sellValues, buyValues },
        setBuyValues,
        setScrollToBuyIndex,
    } = useContext(SellFormContext);

    const [isAddingToQuote, setIsAddingToQuote] = useState(false);

    const formatPrice = formatAsCurrency(
        activeMarket.iso,
        activeMarket.currency
    );

    // Quotes are stored in client side localstorage
    const isInOpenQuote = isServer()
        ? false
        : isInQuote(productData.sku, { sellValues, buyValues });

    const hasOpenQuote = Boolean(sellValues?.[0]?.data ?? buyValues?.[0]?.data);

    const getScrollToBuyIndex = (buyValues: QuoteItemRepeaterValue[]) => {
        const buyIndex = buyValues.findIndex(
            ({ data }) => data?.product?.id === productData.id
        );

        return buyIndex === -1 ? 0 : buyIndex;
    };

    const addProductToQuote = async () => {
        try {
            setIsAddingToQuote(true);

            if (isInOpenQuote || !productData.id || !refreshProductData) {
                return buyValues;
            }

            const { isSold } = await refreshProductData(productData.id);

            if (isSold) {
                return buyValues;
            }

            const buyingItem: QuoteItemRepeaterValue = {
                data: {
                    product: {
                        linkHref: '',
                        price: formatPrice(parseFloat(productData.listPrice)),
                        image: productData.images[0]?.imageUrl,
                        condition: productData.condition,
                        conditionDescription: productData.conditionDescription,
                        description: productData.description,
                        id: productData.id,
                        name: productData.name,
                        sku: productData.sku,
                    },
                },
                id: getNextQuoteItemId(),
            };

            const updatedBuyValues = [...(buyValues ?? []), buyingItem];

            await new Promise<void>((resolve) =>
                setBuyValues(updatedBuyValues, () => resolve())
            );

            return updatedBuyValues;
        } finally {
            setIsAddingToQuote(false);
        }
    };

    const handleTradeForThis = async () => {
        const updatedBuyValues = await addProductToQuote();

        setScrollToBuyIndex(getScrollToBuyIndex(updatedBuyValues));

        router.push(
            '/sell-form',
            `/${activeMarket.slug}/${t('sell-or-trade', {
                ns: 'urls',
            })}`
        );
    };

    return { handleTradeForThis, isAddingToQuote, hasOpenQuote, isInOpenQuote };
};
